import { createSelector } from 'reselect';
import { get, reduce, random, omit, isEmpty, isFunction } from 'lodash';
import {
	getSearchViewPixel,
	getSearchLandingViewPixel,
} from '@sparefoot/react-core/utils/tracking';

import {
	storageStackedUnitBuckets,
	vehicleStackedUnitBuckets,
} from 'config/units';
import faqByPage from 'config/faqByPage';
import { withinCallCenterHours } from 'utils/time/withinCallCenterHours';
import { getQueryParams } from 'store/selectors/application';
import buildSearchLink from 'utils/search/buildSearchLink';
import { getFilterValueForAmenity } from 'utils/search/parseAmenityFromPath';
import { INTERNAL_PAGE_TYPES } from '../../config/constants';

const getFacilityUnits = (facilityUnits, units) =>
	reduce(
		[
			...storageStackedUnitBuckets,
			...vehicleStackedUnitBuckets,
			'recommendedUnit',
		],
		(allUnits, size) => ({
			...allUnits,
			[size]: units[facilityUnits[size]],
		}),
		{ ...facilityUnits },
	);

const getLowestPriceFromUnits = (facilityUnits, units) => {
	const allUnitIds = [
		...(facilityUnits.storageUnits || []),
		...(facilityUnits.vehicleUnits || []),
	];

	const bucketKeys = [
		...storageStackedUnitBuckets,
		...vehicleStackedUnitBuckets,
		'recommendedUnit',
	];
	bucketKeys.forEach((key) => {
		const id = facilityUnits[key];
		if (id != null && !allUnitIds.includes(id)) {
			allUnitIds.push(id);
		}
	});

	const prices = allUnitIds
		.map((id) => get(units, [id, 'price', 'meta']))
		.filter((price) => typeof price === 'number' && price > 0);
	return prices.length ? Math.min(...prices) : null;
};

export const getSearchFacilities = createSelector(
	(state) => state.search.facilities,
	(state) => state.facilities.byId,
	(state) => state.units.byId,
	(state) => state.reviews.byId,
	(state) => state.search.page,
	(state) => state.search.listingsPerPage,
	(facilityIds, facilities, units, reviews, page, listingsPerPage) =>
		facilityIds.map((facilityId, index) => {
			const facility = facilities[facilityId];

			const facilityUnits = get(facility, 'units', {});

			const highestReview = get(reviews, facility.highestReview, null);

			const bestReviews = get(facility, 'bestReviews', [])
				.slice()
				.reverse()
				.map((reviewId) => reviews[reviewId]);

			const facilityRank = get(facility, 'searchRank', null);

			const computedLowestPrice = getLowestPriceFromUnits(
				facilityUnits,
				units,
			);

			return {
				...facility,
				facilityRank,
				index: index + 1,
				position: index + 1 + (page - 1) * listingsPerPage,
				units: getFacilityUnits(facilityUnits, units),
				bestReviews,
				highestReview,
				lowestPrice:
					computedLowestPrice !== null
						? computedLowestPrice
						: facility.lowestPrice,
			};
		}),
);

export const getRecentReservations = createSelector(
	(state) => state.search.locationHash,
	(state, props) => props.cookies,
	(hash, cookies) => {
		const cookieStr = `rr_${hash}`;
		const locationCookie = cookies.get(cookieStr);
		if (locationCookie) {
			return Number.parseInt(locationCookie);
		}
		const reservationValue = random(10, 50);
		cookies.set(cookieStr, reservationValue, { path: '/' });
		return reservationValue;
	},
);

export const getStorageTypeLinks = createSelector(
	(state) => state.search.query,
	(state) => state.search.cityUrl,
	(queryParams, cityUrl) => {
		if (!queryParams.pageType) {
			return {
				storage: buildSearchLink(
					omit(queryParams, ['type', 'vehicle', 'page']),
					true,
				),
				vehicle: buildSearchLink(
					{ ...omit(queryParams, ['page']), type: 'vehicle' },
					true,
				),
			};
		}
		const slash = cityUrl?.slice(-1) === '/' ? '' : '/';
		return {
			storage: cityUrl,
			vehicle: `${cityUrl}${slash}car-storage/`,
		};
	},
);

export const getSearchLocationText = createSelector(
	(state) => state.search.query,
	(state) => get(state.search, 'content.location'),
	(state) => state.search.location,
	(query, contentLocation, searchLocation) => {
		if (query.pageType) {
			if (contentLocation) {
				return `${contentLocation.city}, ${contentLocation.state}`;
			}
			return `${searchLocation.city}, ${searchLocation.state}`;
		}
		return query.location;
	},
);

export const vipCardSelector = createSelector(
	getSearchFacilities,
	(state) => state.search,
	(facilities, search) => {
		const { page, searchType, nearbyFacilities, vipAccounts } = search;
		const isFirstPage = page === 1;
		const firstFacility = isFirstPage && facilities.length && facilities[0];
		const firstFacilityAccountId = get(firstFacility, 'account.id');
		const vipAccount = get(vipAccounts, firstFacilityAccountId, false);
		const vipFacilityId = vipAccount ? firstFacility.id : null;
		const firstListingIsVipElegible =
			isFirstPage && searchType === 'storage' && !!vipAccount;
		return {
			firstListingIsVipElegible,
			vipAccount,
			firstFacilityAccountId,
			nearbyFacilities: nearbyFacilities.filter(
				({ id }) => id !== vipFacilityId,
			),
		};
	},
);

export const faqContentSelector = createSelector(
	(state) => state.search,
	({ location, content, query }) => {
		// Reading from seoBodyJson first
		const seoBodyJson = get(content, 'seoBodyJson', []) || [];
		let faq = [];
		seoBodyJson.forEach((item) => {
			if (item.type === 'faq_section') {
				faq = faq.concat(
					item.value.map((faqItem) => ({
						copy: faqItem.a,
						title: faqItem.q,
					})),
				);
			}
		});

		if (faq.length) {
			return {
				faqContent: faq,
				faqShowing: true,
			};
		}

		let functionPath = get(query, 'pageType');

		if (functionPath === INTERNAL_PAGE_TYPES.VEHICLE_LANDING_PAGE) {
			functionPath += `.${get(query, 'vehicle')}`;
		} else if (functionPath === INTERNAL_PAGE_TYPES.AMENITY_LANDING_PAGE) {
			functionPath += `.${get(query, 'subPageType')}`;
		}

		const faqFunction = get(faqByPage, functionPath, []);

		if (isFunction(faqFunction)) {
			const { city, state } = location;
			const avgPrices = get(content, 'pricingStats.averagePrices');
			const faqContent = faqFunction(city, state, avgPrices);

			return {
				faqContent,
				faqShowing: !!faqContent.length,
			};
		}
		return {};
	},
);

export const headlineSelector = createSelector(
	(state) => state.search,
	({ headline, content, meta }) => {
		const contentTitle = get(content, 'contentTitle');

		// Prefer meta.headline (from the most recent gateway response) over
		// the stale contentTitle set on the initial landing page load.
		// On initial load both values match (same SEO templates). After any
		// filter change, only meta.headline reflects the current query state.
		return {
			headline:
				meta?.headline || contentTitle || headline || 'Self-Storage',
			subtitle: get(content, 'contentSubtitle'),
		};
	},
);

const getSearchComputedData = createSelector(
	getSearchFacilities,
	getStorageTypeLinks,
	getSearchLocationText,
	getQueryParams,
	getRecentReservations,
	faqContentSelector,
	headlineSelector,
	(
		facilities,
		storageTypeUrls,
		searchLocationText,
		queryParams,
		recentReservations,
		faqContent,
		titles,
	) => ({
		facilities,
		storageTypeUrls,
		searchLocationText,
		queryParams,
		recentReservations,
		faqContent,
		titles,
	}),
);

const getSearchState = createSelector(
	(state) => state.search,
	(state) => state.phone,
	(state) => state.user,
	(state) => state.app.baseUrl,
	(state) => state.app.cloudfrontUrl,
	(search, phone, user, baseUrl, cloudfrontUrl) => ({
		search,
		phone,
		user,
		baseUrl,
		cloudfrontUrl,
	}),
);

export default createSelector(
	getSearchComputedData,
	getSearchState,
	(computed, stateData) => {
		const {
			facilities,
			storageTypeUrls,
			searchLocationText,
			queryParams,
			recentReservations,
			faqContent,
			titles,
		} = computed;
		const { search, phone, user, baseUrl, cloudfrontUrl } = stateData;

		// Append price suffix to the page title for storage pages only.
		// !pageType covers the homepage/generic search path — the API omits pageType
		// for those routes. CITY_LANDING_PAGE also gets the suffix.
		const isStoragePage =
			!search.query.pageType ||
			search.query.pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE;
		const baseMeta = {
			...search.meta,
			canonical: baseUrl + search.meta.canonical,
		};
		const titleMissingPrice =
			baseMeta.title && !baseMeta.title.includes('(From $');
		if (
			isStoragePage &&
			titleMissingPrice &&
			search.page === 1 &&
			facilities.length
		) {
			const prices = facilities
				.map((f) => parseFloat(f.lowestPrice))
				.filter((p) => !Number.isNaN(p) && p > 0);
			if (prices.length) {
				baseMeta.title = `${baseMeta.title} (From $${Math.floor(Math.min(...prices))})`;
			}
		}

		return {
			facilities,
			baseUrl,
			cloudfrontUrl,
			statusCode: search.statusCode,
			cityUrl: search.cityUrl,
			errorMessage: search.error,
			facetCounts: search.facetCounts,
			filtersShowing: search.filtersShowing,
			stateUrl: search.stateUrl,
			headline: titles.headline,
			subtitle: titles.subtitle,
			listingsPerPage: search.listingsPerPage,
			loading: search.loading,
			searchLocation: search.location,
			meta: baseMeta,
			page: search.page,
			phone: phone.headerPhone,
			stickyTrayPhoneNumber: phone.stickyTrayPhoneNumber,
			facilityNotFound: !!queryParams.facilityNotFound,
			recentReservations,
			withinCallCenterHours: withinCallCenterHours(),
			visitId: user.visitId,
			deviceType: user.deviceType,
			searchId: search.searchId,
			searchContent: get(search, 'content', {}),
			searchDividerIndex: search.lastInOriginalSearch,
			searchLocationText,
			searchParams: (() => {
				const query = search.query;
				const amenities = query?.amenities
					? [...new Set(query.amenities)]
					: [];

				// On amenity landing pages, the API returns the amenity in the
				// amenities array but not as a boolean param. The old filter UI
				// (FilterGroup) checks the amenities array, but the new filter
				// UI (SearchFiltersVariation) checks boolean params like
				// queryParams.freeTruckRental. Inject the boolean so both UIs
				// show the checkbox as checked.
				const amenitySlug = query?.subPageType;
				const filterValue = amenitySlug
					? getFilterValueForAmenity(amenitySlug)
					: null;

				return {
					...query,
					amenities: amenities.length ? amenities : undefined,
					// For SearchFiltersVariation (new search): set boolean param
					...(filterValue && !query[filterValue]
						? { [filterValue]: true }
						: {}),
				};
			})(),
			searchType: search.searchType,
			showInformedSearch:
				search.query.pageType ===
					INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE && search.page === 1,
			showRecommendedUnit:
				!!search.query.sqft ||
				!!search.query.length ||
				!!search.query.vehicleAccommodation,
			staticMapUrl: search.staticMapUrl,
			totalBookings: get(search, 'content.cityTotalBookings', 0),
			totalListings: search.totalListings,
			totalPages: search.totalPages,
			pageType: search.pageType,
			storageTypeUrls,
			trackingPixel: isEmpty(search.content)
				? getSearchViewPixel(search, search.content)
				: getSearchLandingViewPixel(search, search.content),
			...faqContent,
			redirectURL: search.redirectURL,
			drillDownLinks: search.drillDownLinks || {},
		};
	},
);
